import { observer } from "mobx-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { useMst } from "modules/root/models/hooks";
import { Milestone, MILESTONE_DAY_FORMAT, MilestoneDraft } from "modules/schedules/models/milestones-store";
import { MilestoneDialog } from "modules/schedules/components/MilestoneDialog";
import { milestoneDate } from "modules/schedules/components/MilestonesLayer";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./OrderMilestones.module.scss";

interface OrderMilestonesProps {
    orderId: string;
    /** «0500 Название» — в заголовок формы */
    project: string;
}

/**
 * Блок «Вехи» в карточке проекта. Данные те же, что у ромбов в плановом графике.
 * Править может правка графиков на все проекты или ГИП этого проекта — это решает сервер.
 */
export const OrderMilestones: React.FC<OrderMilestonesProps> = observer(({ orderId, project }) => {
    const { milestones } = useMst();

    const [items, setItems] = useState<Milestone[]>([]);
    const [canEdit, setCanEdit] = useState(false);
    const [dialog, setDialog] = useState<MilestoneDraft | null>(null);

    const isNew = !orderId || orderId === EMPTY_OBJECT_ID;

    const reload = useCallback(async () => {
        if (isNew) {
            setItems([]);
            setCanEdit(false);
            return;
        }

        const data = await milestones.load(orderId);

        if (data) {
            setItems(data.items);
            setCanEdit(data.canEdit);
        }
    }, [milestones, orderId, isNew]);

    useEffect(() => {
        reload();
    }, [reload]);

    const save = useCallback(
        async (draft: MilestoneDraft) => {
            const saved = await milestones.save(draft);
            saved && reload();
            return !!saved;
        },
        [milestones, reload]
    );

    const remove = useCallback(
        async (milestone: Milestone) => {
            const removed = await milestones.remove(milestone);
            removed && reload();
            return removed;
        },
        [milestones, reload]
    );

    // у нового договора вех ещё быть не может — сначала его надо сохранить
    if (isNew) {
        return null;
    }

    return (
        <div className={styles.block}>
            {items.length === 0 && <div className={styles.empty}>Вех нет</div>}

            {items.map((milestone) => (
                <div
                    key={milestone.id}
                    className={styles.item}
                    title={milestone.comment || undefined}
                    onClick={() => setDialog({ ...milestone })}
                >
                    <span className={styles.diamond} />
                    <span className={styles.date}>{milestoneDate(milestone)}</span>
                    <span className={styles.name}>{milestone.name}</span>
                </div>
            ))}

            {canEdit && (
                <PlanrButton
                    type="secondary"
                    size="small"
                    icon="general-plus"
                    className={styles.add}
                    onClick={() =>
                        setDialog({
                            id: "",
                            orderId,
                            day: moment().format(MILESTONE_DAY_FORMAT),
                            name: "",
                            comment: "",
                        })
                    }
                >
                    Добавить веху
                </PlanrButton>
            )}

            {dialog && (
                <MilestoneDialog
                    milestone={dialog}
                    project={project}
                    readOnly={!canEdit}
                    onSave={save}
                    onRemove={remove}
                    onClose={() => setDialog(null)}
                />
            )}
        </div>
    );
});
